import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    Box,
    Paper,
    Typography,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Chip
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadIcon from '@mui/icons-material/Upload';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PublicIcon from '@mui/icons-material/Public';
import StoreIcon from '@mui/icons-material/Store';
import EnhancedTable from '../../../enhancedTable/EnhancedTable';
import { CatalogService } from '../../../../service/catalogService';
import ImportDialog from './ImportDialog';
import CatalogFrameInputForm from './CatalogFrameInputForm';
import CatalogFrameEditDialog from './CatalogFrameEditDialog';
import CatalogFrameImportDialog from './CatalogFrameImportDialog';
import BulkCopyResultDialog from './BulkCopyResultDialog';
import useCatalogFrameColumns from './useCatalogFrameColumns';
import { toast } from 'react-toastify';

const CatalogFrames = () => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [filters, setFilters] = useState([]);
    const [sortBy, setSortBy] = useState([]);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingFrame, setEditingFrame] = useState(null);
    const [catalogImportDialogOpen, setCatalogImportDialogOpen] = useState(false);
    const [catalogScope, setCatalogScope] = useState('local'); // 'global' or 'local'
    const [promotingId, setPromotingId] = useState(null);
    const [bulkPromoting, setBulkPromoting] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [bulkCopyResult, setBulkCopyResult] = useState(null);
    const [resultDialogOpen, setResultDialogOpen] = useState(false);
    const queryClient = useQueryClient();

    // Fetch filter options for the form
    const { data: brandsData } = useQuery('catalogBrands', () => CatalogService.getCatalogBrandsFilter());
    const { data: shapesData } = useQuery('catalogShapes', () => CatalogService.getFrameShapesFilter());
    const { data: materialsData } = useQuery('catalogMaterials', () => CatalogService.getFrameMaterialsFilter());

    // Mutation for promoting frame to tenant catalog
    const promoteMutation = useMutation(
        (frameId) => CatalogService.promoteFrame(frameId),
        {
            onSuccess: () => {
                toast.success('Frame copied to your catalog');
                setPromotingId(null);
                // Invalidate queries to refresh the local catalog view
                queryClient.invalidateQueries(['catalogFrames']);
            },
            onError: (error) => {
                const message = error?.response?.data?.message || 'Failed to copy frame';
                toast.error(message);
                setPromotingId(null);
            }
        }
    );


    // Fetch catalog frames
    const { data, isLoading } = useQuery(
        ['catalogFrames', page, pageSize, filters, sortBy, catalogScope],
        () => {
            const params = {
                page: page + 1, // Backend uses 1-based pagination
                pageSize,
                scope: catalogScope // 'global' or 'local'
            };

            // Add sort if present
            if (sortBy && sortBy.length > 0) {
                const sortField = sortBy[0].id;
                const sortDir = sortBy[0].desc ? '-' : '';
                params.sort = `${sortDir}${sortField}`;
            }

            // Add filter params
            if (filters && Array.isArray(filters)) {
                filters.forEach(filter => {
                    if (filter.id === 'brand' && filter.value?.value) {
                        params.brandId = filter.value.value;
                    } else if (filter.id === 'shape' && filter.value?.value) {
                        params.shapeId = filter.value.value;
                    } else if (filter.id === 'material' && filter.value?.value) {
                        params.materialId = filter.value.value;
                    } else if (filter.id === 'modelNumber' && filter.value) {
                        params.modelNumber = filter.value; // Targeted model number search
                    } else if (filter.id === 'upc' && filter.value) {
                        params.upc = filter.value; // Targeted UPC search
                    } else if (filter.id === 'a' && filter.value) {
                        // Range filter for A measurement
                        const { op, value, value2 } = filter.value;
                        if (op === 'between' && value && value2) {
                            params.aMin = value;
                            params.aMax = value2;
                        } else if (op === 'gt' && value) {
                            params.aMin = value;
                        } else if (op === 'lt' && value) {
                            params.aMax = value;
                        } else if (op === 'eq' && value) {
                            params.aMin = value;
                            params.aMax = value;
                        }
                    } else if (filter.id === 'b' && filter.value) {
                        // Range filter for B measurement
                        const { op, value, value2 } = filter.value;
                        if (op === 'between' && value && value2) {
                            params.bMin = value;
                            params.bMax = value2;
                        } else if (op === 'gt' && value) {
                            params.bMin = value;
                        } else if (op === 'lt' && value) {
                            params.bMax = value;
                        } else if (op === 'eq' && value) {
                            params.bMin = value;
                            params.bMax = value;
                        }
                    } else if (filter.id === 'dbl' && filter.value) {
                        // Range filter for DBL measurement
                        const { op, value, value2 } = filter.value;
                        if (op === 'between' && value && value2) {
                            params.dblMin = value;
                            params.dblMax = value2;
                        } else if (op === 'gt' && value) {
                            params.dblMin = value;
                        } else if (op === 'lt' && value) {
                            params.dblMax = value;
                        } else if (op === 'eq' && value) {
                            params.dblMin = value;
                            params.dblMax = value;
                        }
                    } else if (filter.id === 'temple' && filter.value) {
                        // Range filter for Temple measurement
                        const { op, value, value2 } = filter.value;
                        if (op === 'between' && value && value2) {
                            params.templeMin = value;
                            params.templeMax = value2;
                        } else if (op === 'gt' && value) {
                            params.templeMin = value;
                        } else if (op === 'lt' && value) {
                            params.templeMax = value;
                        } else if (op === 'eq' && value) {
                            params.templeMin = value;
                            params.templeMax = value;
                        }
                    }
                });
            }

            return CatalogService.listFrames(params);
        },
        {
            keepPreviousData: true
        }
    );

    const columns = useCatalogFrameColumns();

    // Build table columns; add source column in global view for clarity
    const tableColumns = useMemo(() => {
        const base = columns.map(col => {
            if (col.id === 'modelNumber') {
                return {
                    ...col,
                    Cell: ({ row }) => (
                        <Typography variant="body2">
                            {row.original?.frame?.modelNumber || row.values?.modelNumber || ''}
                        </Typography>
                    )
                };
            }
            return col;
        });

        if (catalogScope === 'global') {
            return [
                ...base,
                {
                    id: 'source',
                    Header: 'Source',
                    accessor: 'tenantId',
                    Cell: ({ row }) => (
                        row.original.tenantId ? (
                            <Chip label="Local" size="small" icon={<StoreIcon />} variant="outlined" />
                        ) : (
                            <Chip label="Global" size="small" icon={<PublicIcon />} variant="filled" />
                        )
                    )
                }
            ];
        }

        return base;
    }, [columns, catalogScope]);

    const handleImport = (product) => {
        setSelectedProduct(product);
        setImportDialogOpen(true);
    };

    const handleImportSuccess = () => {
        setImportDialogOpen(false);
        setSelectedProduct(null);
        // Optionally show success notification
        toast.success('Frame imported successfully to inventory');
    };

    const handleProductAdded = () => {
        setAddProductDialogOpen(false);
        // Refetch catalog frames to show new product
        // React Query will automatically refetch on next render
    };

    const handlePromote = (frameId) => {
        setPromotingId(frameId);
        promoteMutation.mutate(frameId);
    };

    const handleEditFrame = (frame) => {
        setEditingFrame(frame);
        setEditDialogOpen(true);
    };

    const handleEditSuccess = (updatedFrame) => {
        setEditDialogOpen(false);
        setEditingFrame(null);
        queryClient.invalidateQueries(['catalogFrames']);
    };

    const handleBulkPromote = async (rows = [], helpers = {}) => {
        if (!rows.length) return;
        setBulkPromoting(true);
        try {
            const ids = rows.map(r => r.id);
            const result = await CatalogService.bulkPromoteFrames(ids);
            
            const resultData = result.data || {};
            const { succeeded = [], skipped = [], failed = [] } = resultData;
            
            // Show detailed dialog if there are any issues
            if (skipped.length > 0 || failed.length > 0) {
                setBulkCopyResult(resultData);
                setResultDialogOpen(true);
            } else {
                // Simple success toast for clean copies
                toast.success(`Copied ${succeeded.length} frame${succeeded.length > 1 ? 's' : ''} to your catalog`);
            }
            
            if (helpers.clearSelection) helpers.clearSelection();
            queryClient.invalidateQueries(['catalogFrames']);
        } catch (error) {
            const message = error?.response?.data?.message || 'Failed to copy selected frames';
            toast.error(message);
        } finally {
            setBulkPromoting(false);
        }
    };

    const handleScopeChange = (event, newScope) => {
        if (newScope !== null) {
            setCatalogScope(newScope);
            setPage(0); // Reset to first page when switching scopes
        }
    };

    const handleBulkDelete = async (rows = [], helpers = {}) => {
        if (!rows.length) return;
        setBulkDeleting(true);
        try {
            const ids = rows.map(r => r.id);
            await CatalogService.deleteFrames(ids);
            toast.success(`Deleted ${rows.length} frame${rows.length > 1 ? 's' : ''}`);
            if (helpers.clearSelection) helpers.clearSelection();
            queryClient.invalidateQueries(['catalogFrames']);
        } catch (error) {
            const message = error?.response?.data?.message || 'Failed to delete selected frames';
            toast.error(message);
        } finally {
            setBulkDeleting(false);
        }
    };

    const tableData = data?.data?.data || [];
    const totalCount = data?.data?.count || 0;

    return (
        <Box>
            <Paper sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Frame Catalog
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<UploadIcon />}
                            onClick={() => setCatalogImportDialogOpen(true)}
                        >
                            Import
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setAddProductDialogOpen(true)}
                        >
                            Add Product
                        </Button>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>View:</Typography>
                    <ToggleButtonGroup
                        value={catalogScope}
                        exclusive
                        onChange={handleScopeChange}
                        size="small"
                    >
                        <ToggleButton value="local" aria-label="my catalog">
                            <StoreIcon sx={{ mr: 1 }} />
                            My Catalog
                        </ToggleButton>
                        <ToggleButton value="global" aria-label="global catalog">
                            <PublicIcon sx={{ mr: 1 }} />
                            Global Catalog
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                    {catalogScope === 'local'
                        ? 'Your custom frames. Click "Copy to My Catalog" to add frames from the global catalog.'
                        : 'Browse and copy frames from the global catalog into your inventory'}
                </Typography>
            </Paper>

            <EnhancedTable
                columns={tableColumns}
                data={tableData}
                totalRowCount={totalCount}
                pageCount={Math.ceil(totalCount / pageSize)}
                fetchData={({ pageSize, pageIndex, filters: newFilters, sortBy: newSortBy }) => {
                    setPage(pageIndex);
                    setPageSize(pageSize);
                    setFilters(newFilters || []);
                    setSortBy(newSortBy || []);
                }}
                loading={isLoading}
                selectionMode="multi"
                actions={[
                    ...(catalogScope === 'global' ? [
                        {
                            icon: <FileCopyIcon />,
                            onClick: (row) => handlePromote(row.id),
                            tooltip: 'Copy to My Catalog',
                            disabled: (row) => promotingId === row.id || bulkPromoting
                        }
                    ] : []),
                    ...(catalogScope === 'local' ? [
                        {
                            icon: <EditIcon />,
                            onClick: (row) => handleEditFrame(row),
                            tooltip: 'Edit'
                        },
                        {
                            icon: <AddShoppingCartIcon />,
                            onClick: handleImport,
                            tooltip: 'Import to Inventory'
                        }
                    ] : [])
                ]}
                selectedToolbarActions={[
                    ...(catalogScope === 'global'
                        ? [{
                            id: 'bulk-copy',
                            label: 'Copy to My Catalog',
                            icon: <FileCopyIcon />,
                            tooltip: 'Copy selected frames to My Catalog',
                            variant: 'button',
                            disabled: bulkPromoting,
                            onClick: (selectedRows, helpers) => handleBulkPromote(selectedRows, helpers)
                        }]
                        : []),
                    ...(catalogScope === 'local'
                        ? [{
                            id: 'bulk-delete',
                            label: 'Delete',
                            icon: <DeleteIcon />,
                            tooltip: 'Delete selected catalog frames',
                            variant: 'button',
                            color: 'error',
                            disabled: bulkDeleting,
                            onClick: (selectedRows, helpers) => handleBulkDelete(selectedRows, helpers)
                        }]
                        : [])
                ]}
            />

            {selectedProduct && (
                <ImportDialog
                    open={importDialogOpen}
                    onClose={() => setImportDialogOpen(false)}
                    product={selectedProduct}
                    onSuccess={handleImportSuccess}
                />
            )}

            <CatalogFrameInputForm
                open={addProductDialogOpen}
                onClose={() => setAddProductDialogOpen(false)}
                onRecordAdded={handleProductAdded}
                brands={brandsData?.data || []}
                shapes={shapesData?.data || []}
                materials={materialsData?.data || []}
            />

            <CatalogFrameImportDialog
                open={catalogImportDialogOpen}
                onClose={() => setCatalogImportDialogOpen(false)}
            />

            <BulkCopyResultDialog
                open={resultDialogOpen}
                onClose={() => setResultDialogOpen(false)}
                result={bulkCopyResult}
            />

            {editingFrame && (
                <CatalogFrameEditDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    frame={editingFrame}
                    onSuccess={handleEditSuccess}
                    brands={brandsData?.data || []}
                    shapes={shapesData?.data || []}
                    materials={materialsData?.data || []}
                />
            )}
        </Box>
    );
};

export default CatalogFrames;
